import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Skeleton } from './skeleton';
import { ErrorBoundary } from '../ErrorBoundary';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean; // Add priority prop for preloading important images
}

export const LazyImage = ({ src, alt, className, priority = false, ...props }: LazyImageProps) => {
  const [isLoading, setIsLoading] = useState(!priority);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
    skip: priority,
  });

  useEffect(() => {
    if (priority) {
      setIsLoading(false);
      return;
    }
    if (inView) {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setIsLoading(false);
      };
    }
  }, [inView, src, priority]);
  return (
    <ErrorBoundary
      fallback={
        <div className={`relative ${className} bg-gray-100 flex items-center justify-center`}>
          <span className="text-gray-400">Failed to load image</span>
        </div>
      }
    >
      <div ref={ref} className={`relative ${className}`}>
        {isLoading && (inView || priority) ? (
          <Skeleton className="w-full h-full absolute top-0 left-0" />
        ) : null}
        {(inView || priority) ? (
          <img
            src={src}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
            {...props}
          />
        ) : null}
      </div>
    </ErrorBoundary>
  );
};
